import React, { useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import styles from './MobileMenuItem.scss';
import { offersCategory } from '../offers-category';
import { notifyAnalytics } from '../notify-analytics';
import { formatTestId } from '../../../utils/test-id/format-testid';
import { filterMenuData } from '../../../utils/menu/filterMenuData';
import { ArrowRightOutlined } from 'jl-design-system/components/icons/ArrowRightOutlined';
import { InfoOutlined } from 'jl-design-system/components/icons/InfoOutlined';
import { getThumbnailUrls } from './getThumbnailUrls';
import { mobileNavIconMap } from '../../../utils/icons/mobileNavIconComponents';

const Thumbnail = ({ url, label }) => {
  const thumbnailData = getThumbnailUrls(url);

  return (
    <img
      src={thumbnailData.src}
      srcSet={thumbnailData.srcset}
      alt="" // Deliberately empty alt text as these are decorative images
      className={styles.mobileMenuItem__thumbnailImage}
      width="48"
      height="48"
      data-testid={`nav-thumbnail-${label.toLowerCase().replace(/\s+/g, '-')}`}
    />
  );
};

export const MobileMenuItem = ({
  child = undefined,
  children = undefined,
  className = '',
  hierarchy: parentHierarchy = [],
  isOffersRemoveViewMoreActive = false,
  label,
  onClick = undefined,
  type = '',
  url = '',
  mobileNavThumbnailsActive = false,
  imageUrl = '',
  activeTab = '',
}) => {
  const isButton = child?.length > 0;
  const isHeading = type === 'MENUITEM';
  const isBrandHeading = type === 'BRANDMENUITEM';
  const isAnchor = url && !isButton;
  const isOffer = offersCategory(label, 1);
  const isMoneySection =
    isHeading && label.toLowerCase() === 'john lewis money';
  const isShopOrHelpTab = activeTab === 'Shop' || activeTab === 'Help';
  const isShopTab = activeTab === 'Shop';

  // Check if we should show a thumbnail instead of chevron
  const shouldShowThumbnail =
    mobileNavThumbnailsActive &&
    isButton &&
    !isHeading &&
    !isBrandHeading &&
    activeTab &&
    imageUrl;

  const offersChildren =
    filterMenuData(child, ['MOBILE_WEB'])?.filter(item => !!item.url) || [];
  const hierarchy = [...parentHierarchy, label];
  const sendAnalytics = (tail = []) => {
    notifyAnalytics([...hierarchy, ...tail].join('|'));
  };

  const advanceToChild = e => {
    sendAnalytics();
    onClick(e);
  };

  if (!isAnchor && !isButton && !isHeading && !isBrandHeading) {
    return <></>;
  }

  const getMoreLinkName = moreLabel =>
    `More ${moreLabel
      .toLowerCase()
      .replace(/john lewis/gi, 'John Lewis')
      .replace('our top brands', 'top brands')}`;

  const shouldShowIcon =
    mobileNavThumbnailsActive &&
    (activeTab === 'Help' || activeTab === 'Money');

  const iconMap = useMemo(() => {
    if (!shouldShowIcon) return null;
    return mobileNavIconMap[activeTab];
  }, [shouldShowIcon, activeTab]);

  const getIconComponent = itemLabel => {
    const Component = iconMap ? iconMap[itemLabel] : null;
    if (!Component) {
      return (
        <InfoOutlined className={styles.icon} dataTestId="nav-icon-info" />
      );
    }
    if (typeof Component === 'string') {
      return (
        <img
          src={Component}
          alt={`${itemLabel} icon`}
          className={styles.icon}
          data-testid={`nav-icon-${itemLabel.toLowerCase().replace(/\s+/g, '-')}`}
        />
      );
    }
    return (
      <Component
        className={styles.icon}
        dataTestId={`${formatTestId(itemLabel, 'nav-icon-')}`}
      />
    );
  };

  return (
    <>
      <li
        className={classNames(styles.mobileMenuItem, className, {
          [styles['mobileMenuItem--isShopOrHelpTab']]: isShopOrHelpTab,
        })}
      >
        {isAnchor && (
          <a
            href={url}
            className={classNames(styles.mobileMenuItem__link, {
              [styles.mobileMenuItem__link__bold]: isShopTab,
              [styles['mobileMenuItem--withIcon']]:
                mobileNavThumbnailsActive && activeTab === 'Help',
            })}
            onClick={() => sendAnalytics([])}
            data-testid={formatTestId(label, 'nav-')}
            data-hierarchy={parentHierarchy.join('|')}
          >
            {mobileNavThumbnailsActive &&
              activeTab === 'Help' &&
              getIconComponent(label)}
            {label}
          </a>
        )}
        {isButton && !isHeading && !isBrandHeading && (
          <button
            onClick={advanceToChild}
            data-testid={formatTestId(label, 'nav-')}
            className={classNames(styles.mobileMenuItem__button, {
              [styles.mobileMenuItem__arrow]: !shouldShowThumbnail,
              [styles.mobileMenuItem__thumbnail]: shouldShowThumbnail,
              [styles.offersLabel]: isOffer,
            })}
            aria-label={
              label.endsWith('*')
                ? `${label.replace('*', '')} selected lines only`
                : label
            }
          >
            {label}
            {!shouldShowThumbnail && <ArrowRightOutlined size="medium" />}
            {shouldShowThumbnail && <Thumbnail url={imageUrl} label={label} />}
          </button>
        )}
        {isButton && (isHeading || isBrandHeading) && (
          <div
            className={classNames(styles.buttonHeading, {
              [styles.buttonHeading__moneyHeader]: isMoneySection,
            })}
          >
            <h4>
              {label === 'top brands' ? 'Our ' : ''}
              {label}
            </h4>
          </div>
        )}
        {isOffer && offersChildren.length > 0 && (
          <ul className={styles.offersSubMenu}>
            {offersChildren.slice(0, 3).map(nestedItem => (
              <li className={styles.mobileMenuItem} key={nestedItem.name}>
                <a
                  className={classNames(styles.mobileMenuItem__link)}
                  onClick={() => sendAnalytics([nestedItem.name])}
                  href={nestedItem.url}
                >
                  {nestedItem.name}
                </a>
              </li>
            ))}
            {!isOffersRemoveViewMoreActive && offersChildren.length > 3 && (
              <li
                className={classNames(
                  styles.mobileMenuItem,
                  styles.mobileMenuItem__viewMoreLink
                )}
              >
                <button
                  className={classNames(styles.mobileMenuItem__button)}
                  onClick={advanceToChild}
                >
                  View more
                </button>
              </li>
            )}
          </ul>
        )}
        {isHeading && (
          <ul className={styles.headingSubMenu} data-testid="heading-sub-menu">
            {offersChildren.map(nestedItem => {
              const shouldShowNestedThumbnailImage =
                mobileNavThumbnailsActive && activeTab && nestedItem.imageUrl;
              return (
                <li className={styles.mobileMenuItem} key={nestedItem.name}>
                  <a
                    className={classNames(
                      styles.mobileMenuItem__link,
                      styles.mobileMenuItem__link__tabbedBrandsLink,
                      {
                        [styles.mobileMenuItem__link__withThumbnail]:
                          !!shouldShowNestedThumbnailImage,
                        [styles['mobileMenuItem--withIcon']]:
                          mobileNavThumbnailsActive && activeTab === 'Money',
                      }
                    )}
                    onClick={() => sendAnalytics([nestedItem.name])}
                    href={nestedItem.url}
                    target={isMoneySection ? '_blank' : undefined}
                    rel={isMoneySection ? 'noreferrer' : undefined}
                    data-testid={formatTestId(nestedItem.name, 'services-')}
                  >
                    {mobileNavThumbnailsActive &&
                      activeTab === 'Money' &&
                      getIconComponent(nestedItem.name)}
                    {nestedItem.name}
                    {shouldShowNestedThumbnailImage && (
                      <Thumbnail
                        url={nestedItem.imageUrl}
                        label={nestedItem.name}
                      />
                    )}
                  </a>
                </li>
              );
            })}
          </ul>
        )}
        {isBrandHeading && (
          <ul className={styles.headingSubMenu}>
            {offersChildren.slice(0, 3).map(nestedItem => {
              const shouldShowNestedThumbnailImage =
                mobileNavThumbnailsActive && activeTab && nestedItem.imageUrl;

              return (
                <li
                  className={classNames(styles.mobileMenuItem)}
                  key={nestedItem.name}
                >
                  <a
                    className={classNames(
                      styles.mobileMenuItem__link,
                      styles.mobileMenuItem__link__tabbedBrandsLink,
                      {
                        [styles.mobileMenuItem__link__withThumbnail]:
                          !!shouldShowNestedThumbnailImage,
                      }
                    )}
                    onClick={() => sendAnalytics([nestedItem.name])}
                    href={nestedItem.url}
                    data-testid={formatTestId(nestedItem.name, 'brands-')}
                  >
                    {nestedItem.name}
                    {shouldShowNestedThumbnailImage && (
                      <Thumbnail
                        url={nestedItem.imageUrl}
                        label={nestedItem.name}
                      />
                    )}
                  </a>
                </li>
              );
            })}
            <li className={classNames(styles.mobileMenuItem)}>
              <button
                onClick={advanceToChild}
                data-testid={formatTestId(label, 'nav-')}
                className={classNames(
                  styles.mobileMenuItem__button,
                  styles.mobileMenuItem__chevron,
                  styles.mobileMenuItem__button__nonBoldButton,
                  styles.mobileMenuItem__chevron__tabbedBrandsChevron
                )}
              >
                {getMoreLinkName(label)}
              </button>
            </li>
          </ul>
        )}
        {children}
      </li>
    </>
  );
};

MobileMenuItem.propTypes = {
  label: PropTypes.string.isRequired,
  url: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.func,
  child: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string,
    })
  ),
  hierarchy: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node,
  isOffersRemoveViewMoreActive: PropTypes.bool,
  mobileNavThumbnailsActive: PropTypes.bool,
  type: PropTypes.string,
  activeTab: PropTypes.string,
};
